import { useCallback, useEffect, useRef, useState } from "react";

import appLogger from "@next-platform/client-common-core/logger/appLogger";

import { CustomAudioBufferSourceNode, SocketDataType } from "./types";

const INITIAL_PLAYBACK_DELAY_SECONDS = 0.3;
const MAX_SCHEDULED_BUFFERS = 2;
const textToSpeechLogger = appLogger.withTag("useTextToSpeech");
type NavigatorWithAudioSession = Navigator & { audioSession?: { type: string } };

type TtsSocketPayload = {
  type?: string;
  messageId?: string;
  payloadData?: unknown;
  firstChunk?: boolean;
};

const parseSocketPayload = ({ response }: { response: string }): TtsSocketPayload | null => {
  try {
    const rawPayload = JSON.parse(response) as Record<string, unknown>;
    return {
      type: typeof rawPayload.type === "string" ? rawPayload.type : undefined,
      messageId: typeof rawPayload.messageId === "string" ? rawPayload.messageId : undefined,
      payloadData: rawPayload["data"],
      firstChunk: Boolean(rawPayload.firstChunk),
    };
  } catch (parseError) {
    textToSpeechLogger.error("TTS socket payload parse failed", parseError);
    return null;
  }
};

const useTextToSpeech = () => {
  const refReceivedFirstChunk = useRef(false);
  const refPlaybackStreamComplete = useRef(false);
  const refPendingDecodeCount = useRef(0);
  const refMessageId = useRef("");
  const refPlaybackSessionId = useRef(0);
  const [stateCurrentMessageId, setStateCurrentMessageId] = useState("");
  const [stateIsAudioLoading, setStateIsAudioLoading] = useState(false);
  const refIsAudioLoading = useRef(false);
  const refNextTime = useRef(0);
  const refAudio = useRef<HTMLAudioElement>(new Audio());

  const refAudioStack = useRef<Array<AudioBuffer>>([]);
  const refAudioContext = useRef<AudioContext | null>(null);
  const refSources = useRef<CustomAudioBufferSourceNode[]>([]);
  const refScheduleBufferedAudioChunks = useRef<() => void>(() => {});
  const refTryFinalizeStreamingPlayback = useRef<() => void>(() => {});
  const refStopAudioPlay = useRef<() => void>(() => {});

  const bumpPlaybackSession = useCallback(() => {
    refPlaybackSessionId.current += 1;
  }, []);

  const isPlaybackSessionCurrent = useCallback(({ sessionId, targetMessageId }: { sessionId: number; targetMessageId: string }) => {
    return sessionId === refPlaybackSessionId.current && targetMessageId === refMessageId.current;
  }, []);

  const removeEndedPlaybackSources = useCallback(() => {
    refSources.current = refSources.current.filter((source) => !source.isEnded);
  }, []);

  const countActivePlaybackSources = useCallback(() => {
    removeEndedPlaybackSources();
    return refSources.current.length;
  }, [removeEndedPlaybackSources]);

  const updateAudioLoadingState = useCallback((nextStateIsAudioLoading: boolean) => {
    if (refIsAudioLoading.current === nextStateIsAudioLoading) {
      return;
    }
    refIsAudioLoading.current = nextStateIsAudioLoading;
    setStateIsAudioLoading(nextStateIsAudioLoading);
  }, []);

  const triggerSchedulingAndFinalizeChecks = useCallback(() => {
    refScheduleBufferedAudioChunks.current();
    refTryFinalizeStreamingPlayback.current();
  }, []);

  const stopAllSources = useCallback(() => {
    for (let sourceIndex = 0; sourceIndex < refSources.current.length; sourceIndex += 1) {
      if (refSources.current[sourceIndex]) {
        refSources.current[sourceIndex].stop(0);
      }
    }
    refSources.current = [];
  }, []);

  const stopAudioPlay = useCallback(() => {
    bumpPlaybackSession();
    setStateCurrentMessageId("");
    refMessageId.current = "";
    refPlaybackStreamComplete.current = false;
    refPendingDecodeCount.current = 0;
    if (refAudio.current) {
      refAudio.current.pause();
      refAudio.current.currentTime = 0;
    }
    if (refSources.current.length) {
      stopAllSources();
    }
    refAudioContext.current = null;
    refAudioStack.current = [];
    refNextTime.current = 0;
    refReceivedFirstChunk.current = false;
    updateAudioLoadingState(false);
  }, [bumpPlaybackSession, stopAllSources, updateAudioLoadingState]);

  const onAudioStopped = useCallback(
    (msgId: string) => {
      if (msgId !== refMessageId.current) {
        return;
      }
      stopAudioPlay();
      refReceivedFirstChunk.current = false;
    },
    [stopAudioPlay],
  );

  const tryFinalizeStreamingPlayback = useCallback(() => {
    if (
      !refPlaybackStreamComplete.current ||
      refAudioStack.current.length > 0 ||
      refPendingDecodeCount.current > 0 ||
      countActivePlaybackSources() > 0
    ) {
      return;
    }
    onAudioStopped(refMessageId.current);
  }, [onAudioStopped, countActivePlaybackSources]);

  const scheduleBufferedAudioChunks = useCallback(() => {
    if (!refAudioContext.current) {
      refTryFinalizeStreamingPlayback.current();
      return;
    }

    if (refAudioContext.current.state === "suspended") {
      refAudioContext.current.resume().catch((resumeError) => {
        textToSpeechLogger.error("AudioContext resume failed", resumeError);
      });
    }

    const context = refAudioContext.current;
    const now = context.currentTime;

    if (refNextTime.current === 0) {
      refNextTime.current = now + INITIAL_PLAYBACK_DELAY_SECONDS;
    } else if (refNextTime.current < now) {
      refNextTime.current = now;
    }

    while (refAudioStack.current.length > 0 && countActivePlaybackSources() < MAX_SCHEDULED_BUFFERS) {
      const buffer = refAudioStack.current.shift();
      if (!buffer) {
        break;
      }

      const bufferSource = context.createBufferSource() as CustomAudioBufferSourceNode;
      bufferSource.buffer = buffer;
      bufferSource.connect(context.destination);

      const scheduledStartTime = refNextTime.current;
      bufferSource.start(scheduledStartTime);
      refNextTime.current = scheduledStartTime + buffer.duration;

      refSources.current.push(bufferSource);

      bufferSource.onended = () => {
        bufferSource.isEnded = true;
        removeEndedPlaybackSources();
        refScheduleBufferedAudioChunks.current();
        refTryFinalizeStreamingPlayback.current();
      };
    }

    refTryFinalizeStreamingPlayback.current();
  }, [countActivePlaybackSources, removeEndedPlaybackSources]);

  useEffect(() => {
    refScheduleBufferedAudioChunks.current = scheduleBufferedAudioChunks;
    refTryFinalizeStreamingPlayback.current = tryFinalizeStreamingPlayback;
    refStopAudioPlay.current = stopAudioPlay;
  }, [scheduleBufferedAudioChunks, tryFinalizeStreamingPlayback, stopAudioPlay]);

  useEffect(() => {
    const navigatorWithAudioSession = navigator as NavigatorWithAudioSession;
    if (navigatorWithAudioSession.audioSession) {
      navigatorWithAudioSession.audioSession.type = "playback";
    }

    return () => {
      refStopAudioPlay.current();
    };
  }, []);

  const setMessageId = (id: string) => {
    stopAudioPlay();
    refMessageId.current = id;
    setStateCurrentMessageId(id);
    refReceivedFirstChunk.current = false;
    updateAudioLoadingState(false);
  };

  const handleAudioElementEnded = useCallback(() => {
    onAudioStopped(refMessageId.current);
  }, [onAudioStopped]);

  const playAudioFromURL = useCallback(
    async (url: string) => {
      if (refAudio.current) {
        refAudio.current.pause();
        refAudio.current.currentTime = 0;
        refAudio.current.src = url;
      }
      try {
        await refAudio.current.play();
        refAudio.current.removeEventListener("ended", handleAudioElementEnded);
        refAudio.current.addEventListener("ended", handleAudioElementEnded);
      } catch (playError) {
        textToSpeechLogger.error("TTS URL playback failed", playError);
      }
    },
    [handleAudioElementEnded],
  );

  const handleAudioUrlMessage = useCallback(
    (payload: TtsSocketPayload) => {
      if (typeof payload.payloadData !== "string") {
        return;
      }
      void playAudioFromURL(payload.payloadData);
    },
    [playAudioFromURL],
  );

  const handleChunkMessage = useCallback(
    (payload: TtsSocketPayload) => {
      if (!payload.messageId || !Array.isArray(payload.payloadData) || refMessageId.current !== payload.messageId) {
        return;
      }

      const decodeSessionId = refPlaybackSessionId.current;
      const decodeMessageId = payload.messageId;
      const uint8Array = new Uint8Array(payload.payloadData as number[]);

      if (!refAudioContext.current) {
        refAudioContext.current = new AudioContext();
      }

      refPendingDecodeCount.current += 1;

      const handleDecodeAudioError = () => {
        if (!isPlaybackSessionCurrent({ sessionId: decodeSessionId, targetMessageId: decodeMessageId })) {
          return;
        }
        refPendingDecodeCount.current -= 1;
        triggerSchedulingAndFinalizeChecks();
      };

      void refAudioContext.current.decodeAudioData(
        uint8Array.buffer,
        (buffer) => {
          if (!isPlaybackSessionCurrent({ sessionId: decodeSessionId, targetMessageId: decodeMessageId })) {
            return;
          }
          refPendingDecodeCount.current -= 1;
          refAudioStack.current.push(buffer);
          triggerSchedulingAndFinalizeChecks();
        },
        handleDecodeAudioError,
      );
    },
    [isPlaybackSessionCurrent, triggerSchedulingAndFinalizeChecks],
  );

  const handleDoneMessage = useCallback(
    (payload: TtsSocketPayload) => {
      if (!payload.messageId || refMessageId.current !== payload.messageId) {
        return;
      }

      if (!refReceivedFirstChunk.current) {
        if (typeof payload.payloadData === "string") {
          void playAudioFromURL(payload.payloadData);
        }
        updateAudioLoadingState(false);
        return;
      }

      refPlaybackStreamComplete.current = true;
      updateAudioLoadingState(false);
      triggerSchedulingAndFinalizeChecks();
    },
    [playAudioFromURL, triggerSchedulingAndFinalizeChecks, updateAudioLoadingState],
  );

  const playChatMessage = useCallback(
    async (response: string) => {
      const payload = parseSocketPayload({ response });
      if (!payload?.type) {
        return;
      }

      if (payload.type === SocketDataType.AUDIO_URL) {
        handleAudioUrlMessage(payload);
        return;
      }

      const hasFirstChunkReceivedAlready = payload.firstChunk || refReceivedFirstChunk.current;

      if (payload.type === SocketDataType.CHUNK && payload.firstChunk) {
        refReceivedFirstChunk.current = true;
      }

      if (payload.type === SocketDataType.CHUNK && hasFirstChunkReceivedAlready) {
        handleChunkMessage(payload);
        return;
      }

      if (!refReceivedFirstChunk.current && payload.messageId && refMessageId.current === payload.messageId) {
        updateAudioLoadingState(true);
      }

      if (payload.type === SocketDataType.DONE) {
        handleDoneMessage(payload);
      }
    },
    [handleAudioUrlMessage, handleChunkMessage, handleDoneMessage, updateAudioLoadingState],
  );

  return {
    playChatMessage,
    playAudioFromURL,
    setMessageId,
    currentAudioMessageId: stateCurrentMessageId,
    stopAudioPlay,
    isAudioLoading: stateIsAudioLoading,
  };
};

export default useTextToSpeech;
